<template>
  <WithBackdrop
    :backdrop="data?.fields?.backdrop"
    :class="[$style.config, $style.wrap]"
  >
    <div :class="$style.inner">
      <header :class="$style.header">
        <div :class="$style.avatarWrap">
          <AspectRatio v-if="currentAvatar" ratio="1x1" :class="$style.avatar">
            <BasePicture
              :src="`${currentAvatar?.fields?.image?.fields?.file?.url}?fit=thumb&q=90&w=128`"
              alt=""
            />
          </AspectRatio>
          <BaseButton :class="$style.edit" @click="openModal">
            <IconPencil width="22" height="22" />
          </BaseButton>
        </div>
        <div>
          <BaseHeading size="h3" as="h2" :class="$style.heading">
            {{ computedHeader }}
          </BaseHeading>
          <BaseHeading size="h6" as="h4" :class="$style.subheading">
            {{ computedSubheader }}
          </BaseHeading>
          <div
            v-if="!isGamestopStore && !showLegacyMetrics"
            :class="[$style.metrics, missingData && $style.missingData]"
            :inert="missingData"
            :aria-hidden="missingData ? 'true' : undefined"
          >
            <div :class="$style.tier">
              <BaseHeading size="h4" as="h3">
                {{ computedTierLabel }}
              </BaseHeading>
              <div
                v-tooltip="localizedStrings['profile-hero-tooltip']"
                :class="$style.tooltip"
              >
                <IconCircleQuestionMark
                  width="16"
                  height="16"
                  :class="$style.questionMark"
                />
              </div>
            </div>
            <div :class="$style.trend">
              <div
                :class="[
                  $style.trendValue,
                  trendValue > 0 && $style.positive,
                  trendValue < 0 && $style.negative,
                  trendValue === 0 && $style.neutral,
                ]"
              >
                <IconTrendingUp v-if="trendValue > 0" width="24" height="24" />
                <IconTrendingDown
                  v-else-if="trendValue < 0"
                  width="24"
                  height="24"
                />
                <IconMinus v-else width="24" height="24" />
                {{ computedTrendValue }}
              </div>
              <div
                v-tooltip="localizedStrings['profile-hero-trend-tooltip']"
                :class="$style.tooltip"
              >
                <IconCircleQuestionMark
                  width="16"
                  height="16"
                  :class="$style.questionMark"
                />
              </div>
            </div>
          </div>
        </div>
      </header>
      <div :class="[$style.select, shouldOpenSelect && $style.open]">
        <button :class="$style.selectHeader" @click="toggleSelect">
          <span>
            {{ currentStore?.name }}
          </span>
          <IconChevronDown width="16" height="16" :class="$style.chevron" />
        </button>
        <div :class="$style.selectOptionsWrap">
          <ul :class="$style.selectOptions">
            <li
              v-for="store in stores"
              :key="store?.id"
              :class="[
                $style.item,
                store?.id === currentStore?.id && $style.selected,
              ]"
              @click="() => store && handleSelect(store.id)"
            >
              <span :class="$style.option">
                <span :class="$style.storeName">{{ store?.name }}</span>
                <span :class="$style.storeId">
                  {{ localizedStrings?.['store-id'] || 'Store ID:' }}
                  {{ store?.id }}
                </span>
              </span>
            </li>
          </ul>
        </div>
      </div>
    </div>
  </WithBackdrop>
  <BaseModal
    :is-open="shouldOpenModal"
    overlay-color="rgba(0, 0, 0, 0.5)"
    @request-close="closeModal"
  >
    <div :class="$style.modalWrap">
      <div :class="$style.modal">
        <header :class="$style.modalHeader">
          <BaseHeading as="h4" :class="$style.modalHeading">
            {{ localizedStrings['select-avatar'] }}
          </BaseHeading>
          <button :class="$style.close" @click="closeModal">
            <IconClose width="40" height="40" />
          </button>
        </header>
        <div :class="$style.modalContent">
          <div
            v-for="avatar in computedAvatars"
            :key="avatar?.sys?.id"
            :class="$style.modalAvatarWrap"
            @click="selectAvatar(avatar?.sys?.id)"
          >
            <AspectRatio ratio="1x1" :class="$style.modalAvatar">
              <BasePicture
                :src="`${avatar?.fields?.image?.fields?.file?.url}?fit=thumb&q=90&w=80`"
                alt=""
              />
            </AspectRatio>
            <div
              v-if="selectedAvatar === avatar?.sys?.id"
              :class="$style.checkmark"
            >
              <IconCheckmark width="16" height="16" />
            </div>
          </div>
        </div>
        <div :class="$style.modalFooter">
          <BaseButton @click="applyAvatar">
            {{ localizedStrings['apply'] }}
          </BaseButton>
        </div>
      </div>
    </div>
  </BaseModal>
</template>

<script setup lang="ts">
import type {
  TypeContentBlockWithoutUnresolvableLinksResponse,
  TypeMediaWithoutUnresolvableLinksResponse,
} from '~/types/contentful'

const props = defineProps<{
  data: TypeContentBlockWithoutUnresolvableLinksResponse
}>()

const route = useRoute()
const { locale } = useI18n()

const userStore = useUserStore()
const { stores } = storeToRefs(userStore)

const currentStore = computed(
  () =>
    stores.value?.find((store) => store?.id === arrayAsString(route.query.s)) ||
    stores.value?.[0],
)
const selectedStore = ref(currentStore.value?.id)

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const ufetch = $fetch as typeof $fetch<any>

const testProfileMetricsData = await useTestProfileMetrics()

const missingData = ref(
  !(
    currentStore.value &&
    Boolean(
      testProfileMetricsData.value?.metrics[currentStore.value.id]?.store_tier,
    )
  ),
)

const tier = ref(
  (currentStore.value &&
    testProfileMetricsData.value?.metrics[currentStore.value.id]?.store_tier
      ?.current?.value) ||
    0,
)
const tierOverride = ref<number | null | undefined>(
  currentStore.value &&
    (testProfileMetricsData.value?.metrics[currentStore.value.id]?.store_tier
      ?.override?.effective
      ? testProfileMetricsData.value?.metrics[currentStore.value.id]?.store_tier
          ?.override?.value
      : undefined),
)
const trendValue = ref(
  (currentStore.value &&
    testProfileMetricsData.value?.metrics[currentStore.value.id]?.store_tier
      ?.delta) ||
    0,
)

const shouldOpenSelect = ref(false)
const shouldOpenModal = ref(false)
const currentAvatar = ref<
  TypeMediaWithoutUnresolvableLinksResponse | undefined
>(props.data?.fields?.references?.filter?.(instanceOfMedia)?.[0])

// fetch user avatar data from s3
if (userStore.accountId) {
  const { data: avatarData } = await useAsyncData(
    `avatar-data`,
    async () => {
      return (await ufetch('/api/user-avatar', {
        method: 'GET',
        query: { userId: userStore.accountId },
      })) as {
        avatar: TypeMediaWithoutUnresolvableLinksResponse
      }
    },
    {
      transform(input) {
        return {
          ...input,
          fetchedAt: useUnixTimestamp(),
        }
      },
      // getCachedData: (key) => useCachedData(key), // testing disabling this since it shows old avatar after update
      watch: [() => locale.value],
    },
  )

  // console.log('avatarData', avatarData.value?.avatar)

  // if avatar exists on s3 then set it the currentAvatar
  if (avatarData.value?.avatar) {
    currentAvatar.value = avatarData.value.avatar
  }
}

// set selection to currentAvatar
const selectedAvatar = ref(currentAvatar.value?.sys?.id)

const { localizedStrings } = useLocalizedStrings()

const isGamestopStore = computed(() => checkIsGamestopStore(currentStore.value))

const { showLegacyMetrics } = useLegacyMetricsView()

const computedHeader = computed(() => {
  const header = props.data?.fields?.header

  if (header?.includes(`{{ storeName }}`)) {
    return header.replace(`{{ storeName }}`, currentStore.value?.name || '')
  }

  return header
})

const computedSubheader = computed(() => {
  const subheader = props.data?.fields?.subheader

  if (subheader?.includes(`{{ storeAddress }}`)) {
    return subheader.replace(
      `{{ storeAddress }}`,
      currentStore.value?.address || '',
    )
  }

  return subheader
})

const computedTiers = computed<{ [key: number]: string }>(() => {
  return {
    0: localizedStrings.value['tier0'],
    1: localizedStrings.value['tier1'],
    2: localizedStrings.value['tier2'],
    3: localizedStrings.value['tier3'],
    4: localizedStrings.value['tier4'],
    5: localizedStrings.value['tier5'],
    6: localizedStrings.value['tier6'],
  }
})

const computedTierLabel = computed(() => {
  return computedTiers.value[tierOverride.value || tier.value]
})

const computedTrendValue = computed(() => {
  return trendValue.value ? Math.abs(trendValue.value) : null
})

const computedAvatars = computed(() => {
  return props.data?.fields?.references?.filter?.(instanceOfMedia)
})

const handleSelect = (id: string) => {
  selectedStore.value = id
  shouldOpenSelect.value = false
  setQuery()
}

const toggleSelect = () => {
  shouldOpenSelect.value = !shouldOpenSelect.value
}

const openModal = () => {
  shouldOpenModal.value = true
}

const closeModal = () => {
  shouldOpenModal.value = false
  selectedAvatar.value = currentAvatar.value?.sys?.id
}

const selectAvatar = (avatarId: string) => {
  selectedAvatar.value = avatarId
}

const applyAvatar = async () => {
  const newAvatar = computedAvatars.value?.find(
    (avatar) => avatar?.sys?.id === selectedAvatar.value,
  )

  if (userStore.accountId) {
    try {
      await ufetch('/api/user-avatar', {
        method: 'POST',
        body: {
          userId: userStore.accountId,
          avatar: newAvatar,
        },
      })
    } catch (error) {
      console.error('Failed to save avatar:', error)
    }
  }

  currentAvatar.value = newAvatar
  shouldOpenModal.value = false
}

const setQuery = () => {
  const router = useRouter()
  router.push({
    query: {
      s: selectedStore.value || undefined,
    },
  })
}

watch(
  () => [route.query.s],
  async () => {
    selectedStore.value = currentStore.value?.id

    if (currentStore.value) {
      const storeTierData =
        testProfileMetricsData.value?.metrics?.[currentStore.value.id]
          ?.store_tier

      tier.value = storeTierData?.current?.value || 0
      tierOverride.value = storeTierData?.override?.effective
        ? storeTierData?.override?.value
        : undefined
      trendValue.value = storeTierData?.delta || 0
      missingData.value = !storeTierData
    } else {
      tier.value = 0
      tierOverride.value = undefined
      trendValue.value = 0
      missingData.value = true
    }
  },
  { immediate: true },
)
</script>

<style lang="scss" module>
.config {
  @include breakpoint-variables(
    '--ph-wrap-padding',
    (
      base: 45px 20px,
      md: 60px 20px,
      lg: 60px 20px,
    )
  );
}

.wrap {
  z-index: 1;
  padding: var(--ph-wrap-padding);
  overflow: visible !important;
}

.inner {
  display: flex;
  flex-direction: column-reverse;
  align-items: center;
  gap: 20px;
  max-width: 800px;
  margin: 0 auto;

  @include media('md') {
    flex-direction: row;
    justify-content: space-between;
    gap: 40px;
    max-width: 1500px;
  }
}

.header {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
  text-align: center;
  color: var(--white);
  width: 100%;

  @include media('md') {
    flex-direction: row;
    text-align: left;
  }

  @include media('xl') {
    width: auto;
  }
}

.heading {
  margin-bottom: 10px;
}

.subheading {
  margin-bottom: 10px;
  color: var(--gray-5);

  @include media('md') {
    margin-bottom: 8px;
  }
}

.avatarWrap {
  position: relative;
  width: 128px;
}

.avatar {
  border: 3px solid var(--white);
  border-radius: 9999px;
  overflow: hidden;
}

.edit {
  position: absolute;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  min-width: 0 !important;
  width: 42px;
  height: 42px;
  padding: 0 !important;
  border: 3px solid var(--white) !important;
  border-radius: 9999px !important;
}

.metrics {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 12px;

  @include media('md') {
    justify-content: flex-start;
  }
}

.tier,
.trend {
  display: flex;
  gap: 4px;
}

.tooltip {
  display: flex;
  width: min-content;
  height: min-content;
}

.questionMark {
  color: var(--gray-4);

  &:hover {
    cursor: pointer;
  }
}

.trendValue {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 8px;
  border-radius: 60px;
  font-weight: 700;

  &.positive {
    background: #84e9b3;
    color: #086223;
  }

  &.negative {
    background: #ffa0a6;
    color: #821118;
  }

  &.neutral {
    background: #bdbdbd;
    color: #4f4f4f;
  }
}

.stores {
  max-width: 310px;
  width: 100%;
  font-family: var(--base-font-family);
  font-size: to-rem(16px);

  .storeId,
  .storeName {
    font-weight: 600;
  }

  .storeId {
    color: #828282;
  }

  :global(.single-value) {
    font-family: var(--base-font-family) !important;
    font-weight: 600 !important;
    font-size: to-rem(16px) !important;
  }

  :global(.menu-option) {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }
}

.select {
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 310px;
  height: 46px;
  background: var(--white);
  border-radius: var(--border-radius);
}

.selectHeader {
  all: unset;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  padding: 12px 16px;
  font-weight: 600;
  white-space: nowrap;
  border: 1px solid var(--gray-5);
  border-radius: var(--border-radius);

  span {
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &:hover {
    cursor: pointer;
  }

  .open & {
    border-bottom: 0;
    border-radius: var(--border-radius) var(--border-radius) 0 0;
  }
}

.chevron {
  color: var(--purple);
}

.selectOptionsWrap {
  position: absolute;
  left: 0;
  right: 0;
  top: 45px;
  display: none;
  overflow: hidden;
  padding: 12px 0 0 16px;
  border: 1px solid var(--gray-5);
  border-top: 0;
  border-radius: 0 0 var(--border-radius) var(--border-radius);
  background: var(--white);

  .open & {
    display: block;
  }
}

.selectOptions {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 0 16px 12px 0;
  max-height: 300px;
  overflow-y: scroll;
}

.item {
  display: flex;
  align-items: center;
  gap: 8px;
  white-space: nowrap;

  .option {
    display: flex;
    flex-direction: column;
    overflow: hidden;
    text-overflow: ellipsis;

    &:hover {
      color: var(--purple-light);
      cursor: pointer;
    }

    span {
      overflow: hidden;
      text-wrap: wrap;
    }
  }

  .storeId {
    color: #828282;
  }

  &.selected .storeName {
    font-weight: 600;
    color: var(--purple);
  }
}

.modalWrap {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  padding: 20px;
}

.modal {
  display: block;
  background: #fff;
  padding: 32px 24px;
  border: 3px solid #e3e3e3;
  border-radius: 16px;
  width: 100%;
  max-width: 530px;
}

.modalHeader {
  display: grid;
  grid-template-columns: 48px auto 48px;
  gap: 5px;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 32px;
}

.modalHeading {
  grid-column-start: 2;
}

.close {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  width: 48px;
  height: 48px;
  border: 0;
  background: none;
}

.modalContent {
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  column-gap: 16px;
  row-gap: 24px;
  max-height: 250px;
  overflow-y: auto;
  padding: 4px 0;

  @include media('sm') {
    max-height: none;
    overflow-y: visible;
    padding: 0;
  }
}

.modalAvatarWrap {
  position: relative;
  width: 64px;
  border-radius: 9999px;
  box-shadow: 0 2px 4px rgba(#000, 0.6);

  &:hover {
    cursor: pointer;
  }

  @include media('sm') {
    width: 80px;
  }
}

.modalAvatar {
  border-radius: 9999px;
  overflow: hidden;
}

.checkmark {
  position: absolute;
  top: 0;
  right: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  background: var(--purple);
  width: 32px;
  height: 32px;
  border: 3px solid var(--white);
  border-radius: 9999px;
  color: var(--white);
}

.modalFooter {
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 48px;
}

.missingData {
  filter: blur(10px);
  pointer-events: none;
  opacity: 0.5;
}
</style>
